// The ONLY seam between the marketing site and the backend (the `mara`
// lead tenant at /api/mara). Mirrors immiport's immiApi.js, retargeted to
// our visaGroup/visa keying, with Australian Privacy Act consent and
// first-touch + last-touch UTM attribution. See docs/lead-pipeline-plan.md.
//
// Backend wiring is deferred (D-006/D-008): until the `mara` tenant is live,
// submits fail over to Netlify Forms so a lead is never lost. The fail-over
// only triggers on a true network failure (a thrown error with no .status);
// a backend rejection (has .status) surfaces to the UI.

export const MARA_API_BASE =
  process.env.REACT_APP_MARA_API_BASE || "https://karvon.shop/api/mara";

export const CONSENT_VERSION = "v1-2026-06";

// ---- Attribution (first-touch + last-touch) -------------------------------

const FIRST_TOUCH_KEY = "mara_first_touch";

const readParams = () => {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams("");
  }
};

const buildTouch = () => {
  const p = readParams();
  const touch = {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
    gclid: p.get("gclid") || "",
    fbclid: p.get("fbclid") || "",
    referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    landingPage:
      typeof window !== "undefined" ? window.location.pathname || "" : "",
  };
  return touch;
};

const hasAnyValue = (obj) => Object.values(obj).some((v) => v && v !== "");

// Call once on app load. First-touch wins (never overwritten).
export const captureUtm = () => {
  try {
    if (sessionStorage.getItem(FIRST_TOUCH_KEY)) return;
    const touch = buildTouch();
    if (hasAnyValue(touch)) {
      sessionStorage.setItem(
        FIRST_TOUCH_KEY,
        JSON.stringify({ ...touch, timestamp: new Date().toISOString() }),
      );
    }
  } catch {
    /* sessionStorage unavailable, best effort */
  }
};

const getFirstTouch = () => {
  try {
    const raw = sessionStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// Attribution attached to every lead: first-touch (persisted) + last-touch (now).
const getAttribution = () => ({
  firstTouch: getFirstTouch(),
  lastTouch: { ...buildTouch(), timestamp: new Date().toISOString() },
});

// ---- Consent --------------------------------------------------------------

// Build the stored consent object from form state (Privacy Act, D-008/P-011).
export const buildConsent = ({ privacyAccepted, marketingOptIn = false }) => ({
  privacyAccepted: !!privacyAccepted,
  marketingOptIn: !!marketingOptIn,
  version: CONSENT_VERSION,
  at: new Date().toISOString(),
});

// ---- Fetch wrapper --------------------------------------------------------

async function apiFetch(path, options = {}) {
  const response = await fetch(`${MARA_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const err = new Error(
      body.message || `Request failed (${response.status})`,
    );
    err.status = response.status;
    throw err;
  }
  return body;
}

// ---- Netlify Forms fail-over ----------------------------------------------

const netlifyEncode = (data) =>
  Object.keys(data)
    .map(
      (k) =>
        encodeURIComponent(k) +
        "=" +
        encodeURIComponent(data[k] == null ? "" : data[k]),
    )
    .join("&");

const netlifyFallback = (formName, fields) => {
  try {
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: netlifyEncode({ "form-name": formName, ...fields }),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
  return { success: true, data: { id: null, fallback: "netlify" } };
};

// ---- Public submit functions ----------------------------------------------

// Eligibility quiz lead (the rich source). answers: [{key,question,answer}].
export async function submitApplication({
  name,
  email,
  phone,
  goal,
  visaGroup,
  visa,
  answers,
  indicativeResult,
  priority,
  consent,
  sessionId,
}) {
  const payload = {
    name,
    email,
    phone,
    source: "quiz",
    goal,
    visaGroup,
    visa,
    answers,
    indicativeResult,
    priority,
    consent,
    sessionId,
    utm: getAttribution(),
  };
  try {
    return await apiFetch("/application", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (err.status) throw err;
    return netlifyFallback("quiz", {
      name,
      email,
      phone,
      goal,
      visa: visa || visaGroup || "",
      recommendation: (indicativeResult?.pathways || []).join(", "),
    });
  }
}

// Append later answers to an existing quiz lead (no-op without an id).
export async function appendApplicationAnswers(id, { answers, visaGroup }) {
  if (!id) return { success: true, data: { fallback: true } };
  return apiFetch(`/application/${id}/answers`, {
    method: "PUT",
    body: JSON.stringify({ answers, visaGroup }),
  });
}

// Book a consultation (high-intent).
export async function submitConsultation({
  name,
  email,
  phone,
  visaGroup,
  visa,
  preferredTime,
  timezone,
  message,
  consent,
}) {
  const payload = {
    name,
    email,
    phone,
    source: "consultation",
    visaGroup,
    visa,
    preferredTime,
    timezone,
    message,
    consent,
    utm: getAttribution(),
  };
  try {
    return await apiFetch("/consultation", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (err.status) throw err;
    return netlifyFallback("consultation", {
      name,
      email,
      phone,
      visa: visa || visaGroup || "",
      preferredTime: preferredTime || "",
      timezone: timezone || "",
      message: message || "",
    });
  }
}

// Contact enquiry (lighter).
export async function submitContact({
  name,
  email,
  phone,
  visaGroup,
  message,
  consent,
}) {
  const payload = {
    name,
    email,
    phone,
    source: "contact",
    visaGroup,
    message,
    consent,
    utm: getAttribution(),
  };
  try {
    return await apiFetch("/contact", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (err.status) throw err;
    return netlifyFallback("contact", {
      name,
      email,
      phone,
      visa: visaGroup || "",
      message: message || "",
    });
  }
}
